/**
 * AnnouncementBar.tsx -- Canonical Cohere 2026 announcement bar.
 *
 * Full-width near-black band with centred white micro text.
 * Severity expressed as a 2px bottom-border accent stripe:
 *   info    -> var(--color-slate)
 *   warning -> var(--color-coral)
 *   success -> var(--color-deep-green)
 *
 * Dismissal persists to localStorage under
 * `capicua.announcement-dismissed.<id>`.
 *
 * PRD SS10: Reads from the `announcements` composite (ADR-025 exception
 * to the 12-composite freeze). Renders at the top of the page shell.
 */
import React, { useCallback, useState, type CSSProperties } from "react";
import { Icon } from "../primitives";

export interface Announcement {
  id: string;
  message: string;
  variant?: "info" | "warning" | "success";
  dismissible?: boolean;
  linkUrl?: string;
  linkLabel?: string;
}

export interface AnnouncementBarProps {
  /**
   * Active announcements. Defaults to an empty array when omitted so the
   * bar can be mounted by layout chrome (PageShell) without a data
   * source — until an announcements query lands, callers can render
   * `<AnnouncementBar />` and get a no-op render rather than a
   * TypeError ("announcements is not iterable").
   */
  announcements?: Announcement[];
}

/** Maps severity variant to the 2px bottom-border accent colour. */
const VARIANT_BORDER_COLOR: Record<
  NonNullable<Announcement["variant"]>,
  string
> = {
  info: "var(--color-slate)",
  warning: "var(--color-coral)",
  success: "var(--color-deep-green)",
};

/** localStorage key for a dismissed announcement. */
function dismissKey(id: string): string {
  return `capicua.announcement-dismissed.${id}`;
}

/** Read dismissed IDs from localStorage on mount. */
function readDismissed(announcements: Announcement[]): Set<string> {
  const set = new Set<string>();
  for (const a of announcements) {
    try {
      if (localStorage.getItem(dismissKey(a.id))) {
        set.add(a.id);
      }
    } catch {
      // localStorage unavailable -- treat as not dismissed
    }
  }
  return set;
}

export function AnnouncementBar({ announcements = [] }: AnnouncementBarProps) {
  const [dismissed, setDismissed] = useState<Set<string>>(() =>
    readDismissed(announcements),
  );

  const dismiss = useCallback((id: string) => {
    try {
      localStorage.setItem(dismissKey(id), "1");
    } catch {
      // localStorage unavailable -- still dismiss in-memory
    }
    setDismissed((prev) => new Set(prev).add(id));
  }, []);

  const visible = announcements.filter((a) => !dismissed.has(a.id));
  if (visible.length === 0) return null;

  return (
    <>
      {visible.map((ann) => {
        const variant = ann.variant ?? "info";
        const borderColor = VARIANT_BORDER_COLOR[variant];

        const barStyle: CSSProperties = {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "36px",
          background: "var(--bg-dark)",
          color: "var(--fg-on-dark)",
          fontSize: "var(--fs-micro)",
          borderBottomWidth: "2px",
          borderBottomStyle: "solid",
          borderBottomColor: borderColor,
          position: "relative",
        };

        const textStyle: CSSProperties = {
          textAlign: "center",
          lineHeight: 1,
        };

        const dismissBtnStyle: CSSProperties = {
          position: "absolute",
          right: "var(--space-md)",
          top: "50%",
          transform: "translateY(-50%)",
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--fg-on-dark)",
          opacity: 0.7,
          padding: "var(--space-xxs)",
          lineHeight: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        };

        return (
          <div key={ann.id} style={barStyle} role="status">
            <span style={textStyle}>
              {ann.message}
              {ann.linkUrl && ann.linkLabel ? (
                <>
                  {" "}
                  <a
                    href={ann.linkUrl}
                    style={{
                      color: "var(--fg-on-dark)",
                      textDecoration: "underline",
                    }}
                  >
                    {ann.linkLabel}
                  </a>
                </>
              ) : null}
            </span>
            {ann.dismissible !== false ? (
              <button
                type="button"
                style={dismissBtnStyle}
                onClick={() => dismiss(ann.id)}
                aria-label="Dismiss announcement"
              >
                <Icon name="x" size={14} />
              </button>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

export default AnnouncementBar;
